import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * Tracks horizontal scroll position so a `scroll-fade-x` mask only fades the overflowing
 * side. Extracted from SpacesCarousel / FilterChips (was duplicated) and shared by SwipeRow,
 * which all spread `fadeStyle` for the same both-edges fade. Returns the scroll-container
 * ref, the `fadeStyle` CSS vars, a `measure` callback, and the raw `isAtStart` / `isAtEnd`
 * edge state for a consumer that ever needs a non-default fade geometry. Pass `depKey`
 * (e.g. item count) so the fade re-measures when content changes.
 */
export function useHorizontalScrollFade(depKey?: unknown) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ isAtStart: true, isAtEnd: true });

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollState({
      isAtStart: el.scrollLeft <= 5,
      isAtEnd: el.scrollLeft >= el.scrollWidth - el.clientWidth - 5,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // Observe the container AND each child: `overflow-x: auto` pins the container
    // at a fixed width while its content reflows underneath (late-loading label
    // text, font swaps), so a container-only observer never fires on that reflow.
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    for (const child of el.children) {
      resizeObserver.observe(child);
    }
    return () => {
      el.removeEventListener('scroll', measure);
      resizeObserver.disconnect();
    };
  }, [measure, depKey]);

  const fadeStyle = {
    '--fade-left-end': scrollState.isAtStart ? '0%' : '10%',
    '--fade-right-start': scrollState.isAtEnd ? '100%' : '90%',
  } as CSSProperties;

  return { scrollRef, fadeStyle, measure, ...scrollState };
}
